import { Box, FormControl, FormControlLabel, Grid, Radio } from '@mui/material';
import React, { useMemo } from 'react';
import SelectShrunkenLabel from 'spw-shared/globalComponents/select-shrunked-label';
import { ColorDot } from 'spw-components/worksheet/styles';
import { isMacro } from 'spw-components/worksheet/utils';
import _, { isNumber } from 'lodash';
import {
  CALCULATOR_TYPES,
  GROWTH_CALCULATORS,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MAX_BUILDING_USF,
  MAX_FLOOR_USF,
} from 'spw-constants';
import {
  IDepartment,
  IFloor,
  IProject,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  JustifyStartRow,
  SPWBaseSelect,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents';
import { CalculatorType } from 'spw-redux/modules/calculators/types';
import {
  SpaceTypeLabel,
  MarginBottom16,
  MarginBottom8,
} from 'spw-shared/globalComponents/containers';
import { UsfWithCommasInput } from 'spw-shared/globalComponents/forms';
import { SpaceBetweenColumn, Column } from 'spw-shared/globalComponents/grid';
import { SelectLabel } from 'spw-components/dialogs/save-scenario/styles';
import { useSelector } from 'react-redux';
import {
  projectColors as selectProjectColors,
  selectFirstBuilding,
  selectProjectDepartments,
} from 'spw-redux/modules/projects/selectors';
import { getColor } from 'spw-shared/utils';
import { selectAreaUnit } from 'spw-components/unit-conversion/selectors';
import { ConfigurationSectionTitles } from '../styles';
import { PlainProgramScenario } from '../../../models/Scenario';
import { SaveAsOperation } from './dialog';

const width = '200px';

const subtitle = (
  <ConfigurationSectionTitles marginTop={1} marginBottom={1}>
    WORKSHEET OPTIONS
  </ConfigurationSectionTitles>
);

const VALID_PARSING_OPTIONS_FOR_CALCULATOR_MAP = {
  [GROWTH_CALCULATORS.macroProgramming]: {
    [GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments]: true,
    [GROWTH_CALCULATORS.buildingHeadcountSeatcount]: true,
    [GROWTH_CALCULATORS.floorHeadcountSeatcount]: true,
  },
  [GROWTH_CALCULATORS.buildingHeadcountSeatcount]: {
    [GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments]: true,
    [GROWTH_CALCULATORS.macroProgramming]: true,
    [GROWTH_CALCULATORS.floorHeadcountSeatcount]: true,
  },
  [GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments]: {
    [GROWTH_CALCULATORS.buildingHeadcountSeatcount]: true,
    [GROWTH_CALCULATORS.macroProgramming]: true,
    [GROWTH_CALCULATORS.floorHeadcountSeatcount]: true,
  },
  [GROWTH_CALCULATORS.floorHeadcountSeatcount]: {
    [GROWTH_CALCULATORS.buildingHeadcountSeatcount]: true,
    [GROWTH_CALCULATORS.macroProgramming]: true,
    [GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments]: true,
  },
  [GROWTH_CALCULATORS.buildingUsf]: { [GROWTH_CALCULATORS.floorUsf]: true },
  [GROWTH_CALCULATORS.floorUsf]: { [GROWTH_CALCULATORS.buildingUsf]: true },
};

export const Configuration = ({
  project,
  isDetailed,
  isShared,
  setIsDetailed,
  setIsShared,
  scenario,
  saveAsOperation,
  setSaveAsOperation,
  calculatorType,
  setCalculatorType,
  selectedDepartment,
  setSelectedDepartment,
  selectedFloor,
  setSelectedFloor,
  setSelectedFloorUSF,
  selectedFloorUSF,
  selectedBuildingUSF,
  setSelectedBuildingUSF,
}: {
  project: IProject;
  isDetailed: boolean;
  isShared: boolean;
  setIsDetailed: (newValue: boolean) => void;
  setIsShared: (newValue: boolean) => void;
  scenario: PlainProgramScenario;
  saveAsOperation: SaveAsOperation;
  setSaveAsOperation: (opType: SaveAsOperation) => void;
  calculatorType?: CalculatorType;
  setCalculatorType: (calculatorType: CalculatorType) => void;
  selectedDepartment?: IDepartment;
  setSelectedDepartment: (dept: IDepartment) => void;
  selectedFloor?: IFloor;
  setSelectedFloor: (floor: IFloor) => void;
  setSelectedFloorUSF: (usf: number) => void;
  selectedFloorUSF?: number;
  setSelectedBuildingUSF: (usf: number) => void;
  selectedBuildingUSF?: number;
}) => {
  const departments = useSelector(selectProjectDepartments);
  const building = useSelector(selectFirstBuilding);
  const projectColors = useSelector(selectProjectColors);
  const areaUnit = useSelector(selectAreaUnit);

  // TODO: Move this code into a hook
  const { sharedCategories, individualWorkspacesCategory } = useMemo(() => {
    return project.spaceTypeCategories.reduce(
      (object, projectSpaceType: IProjectSpaceTypeCategory) => {
        if (
          projectSpaceType.defaultSpaceTypeCategory.category ===
          INDIVIDUAL_WORKSPACES_CATEGORY
        ) {
          return {
            ...object,
            individualWorkspacesCategory: projectSpaceType,
          };
        }

        return {
          ...object,
          sharedCategories: object.sharedCategories.concat([projectSpaceType]),
        };
      },
      { sharedCategories: [], individualWorkspacesCategory: null }
    );
  }, [project.spaceTypeCategories]);

  const floorUSFIsRequired =
    saveAsOperation === SaveAsOperation.copyWithDifferentCalculator &&
    selectedFloor &&
    !selectedFloor.totalFloorUsf &&
    calculatorType === GROWTH_CALCULATORS.floorUsf;

  const buildingUSFISRequired =
    saveAsOperation === SaveAsOperation.copyWithDifferentCalculator &&
    calculatorType === GROWTH_CALCULATORS.buildingUsf &&
    !building?.programmingUsf;

  const saveAsOptionsShouldBeVisible = !!scenario?.id;

  return (
    <SpaceBetweenColumn>
      <SpaceBetweenColumn>
        {saveAsOptionsShouldBeVisible && (
          <>
            <ConfigurationSectionTitles>
              SAVE AS OPTIONS
            </ConfigurationSectionTitles>
            <FormControlLabel
              label={<SelectLabel>Save a copy</SelectLabel>}
              labelPlacement="end"
              style={{ minWidth: width }}
              control={
                <Radio
                  checked={saveAsOperation === SaveAsOperation.copy}
                  onChange={() => setSaveAsOperation(SaveAsOperation.copy)}
                  aria-label="Save a copy"
                />
              }
            />
            <Box
              marginBottom={
                saveAsOperation === SaveAsOperation.copyWithDifferentCalculator
                  ? undefined
                  : 2
              }
            >
              <FormControlLabel
                label={
                  <SelectLabel>
                    Save and move the copy to another scenario type
                  </SelectLabel>
                }
                labelPlacement="end"
                style={{ minWidth: width }}
                control={
                  <Radio
                    checked={
                      saveAsOperation ===
                      SaveAsOperation.copyWithDifferentCalculator
                    }
                    onChange={() =>
                      setSaveAsOperation(
                        SaveAsOperation.copyWithDifferentCalculator
                      )
                    }
                    aria-label="Save As and move the copy to another scenario type"
                  />
                }
              />
            </Box>
            {saveAsOperation ===
              SaveAsOperation.copyWithDifferentCalculator && (
              <Box marginTop={1} marginBottom={4} width="100%">
                <FormControl variant="outlined" fullWidth>
                  <SelectShrunkenLabel>Scenario Type</SelectShrunkenLabel>
                  <SPWBaseSelect
                    label="Scenario Type"
                    placeholder="Select a scenario type"
                    displayEmpty
                    value={calculatorType || ''}
                    MenuProps={{ disableScrollLock: true }}
                    onChange={(value) => {
                      setCalculatorType(value.id as CalculatorType);
                    }}
                    items={CALCULATOR_TYPES.map((i) => ({
                      ...i,
                      label: i.label.replace('USF', areaUnit),
                    }))}
                    getItemIsDisabled={(c) =>
                      !VALID_PARSING_OPTIONS_FOR_CALCULATOR_MAP[
                        scenario?.calculatorType
                      ][c.id]
                    }
                    renderItem={({ label }) => label}
                    data-testid="scenario-type-dropdown"
                  />
                </FormControl>
              </Box>
            )}
            {buildingUSFISRequired && (
              <Box marginBottom={4} width="100%">
                <FormControl variant="outlined" fullWidth>
                  <UsfWithCommasInput
                    label={`Building ${areaUnit}`}
                    style={{ width: '100%' }}
                    max={MAX_BUILDING_USF}
                    setUsfAsNumber={(val: number) =>
                      setSelectedBuildingUSF(_.min([val, MAX_BUILDING_USF]))
                    }
                    value={selectedBuildingUSF}
                  />
                </FormControl>
              </Box>
            )}
            {calculatorType ===
              GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments &&
              saveAsOperation ===
                SaveAsOperation.copyWithDifferentCalculator && (
                <Box marginBottom={4} width="100%">
                  <FormControl variant="outlined" fullWidth>
                    <SelectShrunkenLabel>Departments</SelectShrunkenLabel>
                    <SPWBaseSelect<IDepartment>
                      name="department"
                      data-testid="department-dropdown"
                      label="Departments"
                      placeholder="Select a department"
                      value={selectedDepartment}
                      style={{
                        width: '100%',
                      }}
                      onChange={setSelectedDepartment}
                      items={departments}
                      renderItem={(department) => (
                        <span>
                          <strong style={{ fontWeight: 500 }}>
                            {department.name}
                          </strong>
                          &nbsp;
                          {department.secondaryIdentifier || ''}
                        </span>
                      )}
                    />
                  </FormControl>
                </Box>
              )}
            {saveAsOperation === SaveAsOperation.copyWithDifferentCalculator &&
              [
                GROWTH_CALCULATORS.floorHeadcountSeatcount,
                GROWTH_CALCULATORS.floorUsf,
              ].includes(calculatorType) && (
                <Box marginBottom={4} width="100%">
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <FormControl variant="outlined" fullWidth>
                        <SelectShrunkenLabel>Floor Name</SelectShrunkenLabel>
                        <SPWBaseSelect
                          name="floor"
                          label="Floor Name"
                          value={selectedFloor || null}
                          renderItem={({ name }) => name}
                          onChange={(floor) => setSelectedFloor(floor)}
                          items={building?.floors || []}
                          placeholder="Select a floor"
                          data-testid="floor-dropdown"
                        />
                      </FormControl>
                    </Grid>
                    {floorUSFIsRequired && (
                      <Grid item xs={6}>
                        <FormControl variant="outlined" fullWidth>
                          <UsfWithCommasInput
                            label={`Total Available Floor ${areaUnit}`}
                            style={{ width: '100%' }}
                            max={MAX_FLOOR_USF}
                            placeholder={`${areaUnit} is required`}
                            setUsfAsNumber={(val: number) => {
                              if (isNumber(val))
                                setSelectedFloorUSF(
                                  _.min([val, MAX_FLOOR_USF])
                                );
                            }}
                            value={selectedFloorUSF}
                            dataTestId="floor-usf-input"
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
          </>
        )}
        {subtitle}
        {!!calculatorType || saveAsOperation === SaveAsOperation.copy ? (
          <>
            <MarginBottom16>
              <JustifyStartRow>
                <ColorDot
                  color={getColor(
                    INDIVIDUAL_WORKSPACES_CATEGORY,
                    projectColors
                  )}
                  className="colorDot"
                />
                <SpaceTypeLabel>
                  {individualWorkspacesCategory.name}
                </SpaceTypeLabel>
              </JustifyStartRow>
            </MarginBottom16>
            <SpaceBetweenRow style={{ marginBottom: '15px' }}>
              <FormControlLabel
                label={
                  <Column>
                    <SelectLabel>Summary</SelectLabel>
                    <SelectLabel>
                      {individualWorkspacesCategory.name}
                    </SelectLabel>
                  </Column>
                }
                labelPlacement="end"
                style={{ minWidth: width }}
                control={
                  <Radio
                    checked={!isDetailed}
                    onChange={() => setIsDetailed(false)}
                    aria-label="Summary Individual Workspaces"
                  />
                }
              />
              <FormControlLabel
                label={
                  <Column>
                    <SelectLabel>Detailed</SelectLabel>
                    <SelectLabel>{`${individualWorkspacesCategory.name}`}</SelectLabel>
                  </Column>
                }
                labelPlacement="end"
                style={{ minWidth: width }}
                control={
                  <Radio
                    checked={isDetailed}
                    onChange={() => setIsDetailed(true)}
                    aria-label="Detailed Individual Workspaces"
                  />
                }
              />
            </SpaceBetweenRow>
          </>
        ) : (
          <Box marginBottom={6}>
            Select a scenario type to see the worksheet options.
          </Box>
        )}
      </SpaceBetweenColumn>
      {((isMacro(scenario) && saveAsOperation === SaveAsOperation.copy) ||
        calculatorType === GROWTH_CALCULATORS.macroProgramming) && (
        <SpaceBetweenColumn>
          {sharedCategories.map(
            (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
              return (
                <MarginBottom8 key={projectSpaceTypeCategory.id}>
                  <JustifyStartRow>
                    <ColorDot
                      color={getColor(
                        projectSpaceTypeCategory.defaultSpaceTypeCategory
                          .category,
                        projectColors
                      )}
                      className="colorDot"
                    />
                    <SpaceTypeLabel>
                      {projectSpaceTypeCategory.name}
                    </SpaceTypeLabel>
                  </JustifyStartRow>
                </MarginBottom8>
              );
            }
          )}

          <SpaceBetweenRow style={{ marginBottom: 27, marginTop: 16 }}>
            <FormControlLabel
              label="Group together as Shared Spaces"
              labelPlacement="end"
              style={{ width }}
              control={
                <Radio
                  checked={isShared}
                  onChange={() => setIsShared(true)}
                  aria-label="Group together as Shared Spaces"
                />
              }
            />
            <FormControlLabel
              label="Show all categories separately"
              labelPlacement="end"
              style={{ width }}
              control={
                <Radio
                  checked={!isShared}
                  onChange={() => setIsShared(false)}
                  aria-label="Show all categories separately"
                />
              }
            />
          </SpaceBetweenRow>
        </SpaceBetweenColumn>
      )}
    </SpaceBetweenColumn>
  );
};
